import React, { useMemo } from 'react';
import { Tag } from 'antd';
import { AdvancedFilters } from '../advanced_filters/AdvancedFilters';
import SelectedFilters from '../selected_filters_pills/SelectedFilters';
import SupplierRegionsLocationsTreeSelect from '../regions_tree_select/SupplierRegionsLocationsTreeSelect';
import { changeFilterValueToArrayOfIds, nullSafeGet } from '../../utils/DataAccessUtils';
import { getContactName } from '../../utils/DataFormatterUtils';
import { FILTER_CONFIG_NAMES, FILTER_VALUE_TYPE } from '../../utils/DataConstants';
import { DateRange } from './WrenchModeLogsSidebar';

const FILTERS_TARGET_COLLECTION = 'wrenchModeLogsFilters';
const TECH_TARGET_COLLECTION = 'wrenchModeLogsTechFilter';
const LOCATION_TARGET_COLLECTION = 'wrenchModeLogsLocationFilter';
const SUPERVISOR_TARGET_COLLECTION = 'wrenchModeLogsSupervisorFilter';

export interface WrenchModeLogsFilters {
	[FILTER_CONFIG_NAMES.TECHNICIAN]?: string;
	[FILTER_CONFIG_NAMES.SUPERVISOR]?: string;
	[FILTER_CONFIG_NAMES.LOCATION]?: string;
}

interface WrenchModeLogsHeaderProps {
	filters: WrenchModeLogsFilters;
	updateFilters: (newFilters: Record<string, any>) => void;
	clearAndUpdateFilters: (newFilters: Record<string, any>) => void;
	fieldTechsState: any;
	fetchFieldTechs: (
		params: any,
		targetCollectionName: string,
		pagination?: any,
		sorting?: any,
		filters?: any,
		addToTargetCollection?: boolean
	) => void;
	fetchMultipleFieldTechs: (ids: string[], targetCollectionName?: string) => void;
	allContactsState: any;
	fetchSupervisors: (
		params: any,
		targetCollectionName: string,
		pagination?: any,
		sorting?: any,
		filters?: any,
		addToTargetCollection?: boolean
	) => void;
	dateRange?: DateRange;
	isDateFiltered?: boolean;
	onClearDateFilter?: () => void;
}

const formatDatePill = (range: DateRange): string => {
	if (range.from.isSame(range.to, 'day')) {
		return 'Date: ' + range.from.format('D MMM, YYYY');
	}
	return 'Date is ' + range.from.format('D MMM, YYYY') + ' - ' + range.to.format('D MMM, YYYY');
};

const WrenchModeLogsHeader: React.FC<WrenchModeLogsHeaderProps> = ({
	filters,
	updateFilters,
	clearAndUpdateFilters,
	fieldTechsState,
	fetchFieldTechs,
	fetchMultipleFieldTechs,
	allContactsState,
	fetchSupervisors,
	dateRange,
	isDateFiltered,
	onClearDateFilter,
}) => {
	const filterConfig = useMemo(
		() => [
			{
				label: 'Supervisor',
				fieldName: FILTER_CONFIG_NAMES.SUPERVISOR,
				placeholder: 'Search for a supervisor',
				stateSlice: allContactsState,
				fetch: fetchSupervisors,
				renderItem: (record: any) =>
					getContactName(nullSafeGet('contact', record)).trim() ||
					nullSafeGet('contact.email', record),
				keyAccessor: (record: any) => record.email,
				valueAccessor: (record: any) => record.email,
				sortBy: { sort_by: 'name', order: 'ascend' },
				valueType: FILTER_VALUE_TYPE.STRING,
				mode: null,
				targetCollectionName: SUPERVISOR_TARGET_COLLECTION,
				filtersValueAccessor: (filtersStateSlice: any) =>
					nullSafeGet(FILTER_CONFIG_NAMES.SUPERVISOR, filtersStateSlice) || undefined,
				handleChange: (val: any) => {
					const email = val ? val.key || val : undefined;
					updateFilters({ [FILTER_CONFIG_NAMES.SUPERVISOR]: email });
				},
			},
			{
				label: 'Technician',
				fieldName: FILTER_CONFIG_NAMES.TECHNICIAN,
				placeholder: 'Search for a technician',
				stateSlice: fieldTechsState,
				fetch: fetchFieldTechs,
				fetchMultiple: fetchMultipleFieldTechs,
				renderItem: (fieldTech: any) => fieldTech.name || fieldTech.email,
				keyAccessor: (fieldTech: any) => fieldTech.email,
				valueAccessor: (fieldTech: any) => fieldTech.email,
				sortBy: { sort_by: 'name', order: 'ascend' },
				valueType: FILTER_VALUE_TYPE.STRING,
				mode: null,
				targetCollectionName: TECH_TARGET_COLLECTION,
				filtersValueAccessor: (filtersStateSlice: any) =>
					nullSafeGet(FILTER_CONFIG_NAMES.TECHNICIAN, filtersStateSlice) || undefined,
				handleChange: (val: any) => {
					const email = val ? val.key || val : undefined;
					updateFilters({ [FILTER_CONFIG_NAMES.TECHNICIAN]: email });
				},
			},
			{
				label: 'Location',
				fieldName: FILTER_CONFIG_NAMES.LOCATION,
				placeholder: 'Search for locations',
				mode: 'multiple',
				targetCollectionName: LOCATION_TARGET_COLLECTION,
				filtersValueAccessor: (filtersStateSlice: any) =>
					changeFilterValueToArrayOfIds(
						nullSafeGet(FILTER_CONFIG_NAMES.LOCATION, filtersStateSlice)
					),
				handleChange: (ids: any[]) => {
					updateFilters({
						[FILTER_CONFIG_NAMES.LOCATION]: ids && ids.length > 0 ? ids.join(',') : undefined,
					});
				},
				component: SupplierRegionsLocationsTreeSelect,
			},
		],
		[
			fieldTechsState,
			fetchFieldTechs,
			fetchMultipleFieldTechs,
			allContactsState,
			fetchSupervisors,
			updateFilters,
		]
	);

	return (
		<div className="flex flex-col gap-s">
			<div className="flex items-center gap-s">
				<div className="h4medium text-character-primary">Work Logs</div>
				<AdvancedFilters
					filtersTargetCollectionName={FILTERS_TARGET_COLLECTION}
					filtersStateSlice={filters}
					filterConfig={filterConfig}
					updateFilters={updateFilters}
					clearAndUpdateFilters={clearAndUpdateFilters}
					showSearch={false}
					preAppliedFilters={{}}
				/>
			</div>
			<div className="flex items-center">
				{isDateFiltered && dateRange && onClearDateFilter && (
					<Tag closable onClose={onClearDateFilter} style={{ fontSize: 14, padding: '4px 12px' }}>
						{formatDatePill(dateRange)}
					</Tag>
				)}
				<div className="flex-1">
					<SelectedFilters
						updateFilters={updateFilters}
						filterValues={filters}
						filterConfig={filterConfig}
					/>
				</div>
			</div>
		</div>
	);
};

export default WrenchModeLogsHeader;
